'use client'

import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react'
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import _ from 'lodash'
import type React from 'react'
import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

type ListBoxProps = {
  label?: string | React.ReactNode
  option?: {
    popular?: string[]
    other?: string[]
    all?: string[]
  }
  allowAny?: boolean
  value?: string | number
  onChange: (val: string | number) => void
  optional?: boolean
  disabled?: boolean
} & React.ComponentPropsWithoutRef<'select'>

export function VehicleMakeModelListBox(props: ListBoxProps) {
  const { label, option, allowAny, value, onChange, disabled } = props
  const errMsg = _.get(props, 'data-error')
  const hasPopular = !_.isEmpty(option?.popular)
  const hasOther = !_.isEmpty(option?.other)

  const {
    refs,
    floatingStyles,
    placement: _placement,
  } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(2),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start'],
        fallbackAxisSideDirection: 'start',
        padding: 8,
      }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxWidth: '400px',
          })
        },
        padding: 8,
      }),
    ],
  })
  return (
    <Listbox value={value} onChange={(val) => onChange(val)} disabled={disabled || false}>
      <div className='w-full'>
        {label && <Label className='form-label'>{label}</Label>}
        <div className={twMerge('relative w-full', label && 'mt-1')}>
          <ListboxButton
            ref={refs.setReference}
            className={twMerge('form-listbox rounded pr-10 text-left md:h-9!', disabled && 'form-disabled')}
          >
            <span className='block truncate pr-8 md:text-sm md:font-medium'>{value}</span>
            <span className='form-input-icon'>
              <HiChevronUpDown aria-hidden='true' className='size-5 text-zinc-400' />
            </span>
          </ListboxButton>
          <ListboxOptions ref={refs.setFloating} style={floatingStyles} transition className='form-list-box-options'>
            {allowAny && (
              <ListboxOption value={''} className='form-option'>
                <span className='block truncate font-normal group-data-selected:font-semibold'>Any</span>
              </ListboxOption>
            )}
            {hasPopular && (
              <>
                {hasOther && (
                  <div role='presentation' className='form-option-name'>
                    Popular:
                  </div>
                )}
                {_.map(option?.popular, (val, idx) => (
                  <ListboxOption key={idx} value={val} className={twMerge('form-option mr-4 rounded', hasOther && 'ml-4')}>
                    <span className='block truncate font-normal group-data-selected:font-semibold'>{val}</span>
                  </ListboxOption>
                ))}
              </>
            )}
            {hasOther && (
              <>
                {hasPopular && (
                  <div role='presentation' className='form-option-name'>
                    Other:
                  </div>
                )}
                {_.map(option?.other, (val, idx) => (
                  <ListboxOption key={idx} value={val} className={twMerge('form-option mr-4 rounded', hasPopular && 'ml-4')}>
                    <span className='block truncate font-normal group-data-selected:font-semibold'>{val}</span>
                  </ListboxOption>
                ))}
              </>
            )}
            {!_.isEmpty(option?.all) &&
              _.map(option?.all, (val, idx) => (
                <ListboxOption key={idx} value={val} className='form-option rounded py-1!'>
                  <span className='block truncate font-normal group-data-selected:font-semibold'>{val}</span>
                </ListboxOption>
              ))}
          </ListboxOptions>
        </div>
        {errMsg && (
          <div role='alert' className='form-error-message'>
            {errMsg}
          </div>
        )}
      </div>
    </Listbox>
  )
}
